import { useEffect, useState } from 'react';
import { api, asList } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getCampus, setCampus, ALL_CAMPUSES } from '../lib/campus';
import { isStaffRole } from '../lib/roles';

/**
 * WHICH CAMPUS AM I LOOKING AT — one control, in the top bar.
 *
 * ⚠ ONE PICKER, NOT ONE PER PAGE. There are 115 screens. A dropdown repeated on each of them is 115
 * things to keep in sync and a 116th to forget, and it makes the answer to "which college is this?"
 * a property of wherever you happen to be standing. The campus is a property of the SESSION, so it
 * lives beside the school name, is remembered across pages and reloads, and travels on every
 * request as a header.
 *
 * ═══ WHAT CHANGED, AND WHY THE OLD VERSION WAS WRONG ═══
 *
 * ⚠ IT USED TO ASK "IS THIS USER A TrustAdmin?" — a question that encodes an assumption which turned
 * out to be false: that everybody is either pinned to one campus or entitled to all of them. A trust
 * running a State-board school and a CBSE school as SEPARATE institutions, with ONE headmaster over
 * both and a PU college beside them under somebody else, is neither. He needs to switch between two,
 * and must never be offered the third.
 *
 * So the question is no longer the ROLE. It is HOW MANY CAMPUSES THIS PERSON MAY SEE — and the
 * answer comes from the server, because `/api/institutions` now returns only what the caller is
 * permitted. Nothing here decides access; this control cannot show a campus the API did not send.
 *
 * ⚠ ONE CAMPUS RENDERS AS TEXT, NOT AS A DISABLED DROPDOWN. A disabled control says "you are not
 * allowed", which is the wrong message for somebody who simply has one school — it invites them to
 * wonder what is inside it. A plain label says "this is where you are", which is the true one. On a
 * single-institution trust it renders nothing at all: there is no question to answer.
 */

interface Institution {
  id: string; name: string; type?: string | null;
  /**
   * ⚠ THE PLACE. A trust with institutions in several towns runs "…English Medium School" in four
   * of them — four rows reading almost identically, with nothing on any of them saying which is
   * which. The campus is what tells them apart, and the server now sends it.
   */
  campusName?: string | null; campusCity?: string | null;
}

export function CampusPicker() {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const [list, setList] = useState<Institution[]>([]);
  const [chosen, setChosen] = useState<string>(() => getCampus() ?? ALL_CAMPUSES);

  useEffect(() => {
    /**
     * ⚠ ASKED FOR EVERY MEMBER OF STAFF, not only for a role that "may choose". The server is what
     * narrows the list, so asking on behalf of a teacher is safe and returns their one campus —
     * which is what lets this render their school's name instead of nothing.
     *
     * ⚠ BUT NOT FOR A PARENT, AND THAT CLAUSE IS NOT TIDINESS. `/api/institutions` requires the
     * "staff" policy. This component renders in AppShell for everyone, so it used to ask
     * unconditionally and take a 403 on every page load for every parent and every security guard.
     * The catch below swallowed it, so nothing was visibly broken — it simply wrote an
     * authorisation failure to the server log a few times a second, forever, which is how a real
     * one becomes impossible to notice.
     *
     * ⚠ THE ROLE ARRIVES LATE, so this depends on `role`. With an empty deps array the guard would
     * run once against the empty string, decline to fetch, and never try again — a picker that
     * never appears for anyone.
     */
    if (!isStaffRole(role)) { setList([]); return; }

    let alive = true;
    /**
     * ⚠ asList: an unstubbed or failing endpoint answers `{}`, which is truthy, and `({}).length`
     * is undefined rather than throwing — so a bad response would silently render as if the trust
     * had no campuses at all. That exact shape has broken six screens in this codebase.
     */
    api.get<Institution[] | { items: Institution[] }>('/api/institutions')
      .then((r) => { if (alive) setList(asList(r)); })
      .catch(() => { if (alive) setList([]); });
    return () => { alive = false; };
  }, [role]);

  // Nothing to say: a single-institution trust, or the list has not arrived.
  if (list.length === 0) return null;

  /**
   * ⚠ ONE CAMPUS: A LABEL. Worth showing rather than hiding — in a trust with several institutions,
   * a teacher who can see only theirs should still be told which one they are in, because every
   * number on every screen is about that campus and nothing else on the page says so.
   *
   * ⚠ BUT NEVER WHEN A CAMPUS IS ALREADY SELECTED, and that clause is not defensive tidiness — it is
   * a trap I shipped. The server briefly narrowed this list to the CURRENT SELECTION rather than to
   * the caller's entitlement, so choosing a campus collapsed the list to one, the control turned
   * into a label, and there was no way back to "all campuses" except clearing browser storage.
   *
   * The server side is fixed. This clause means that even if some future response comes back short
   * for any reason, a user who has made a choice can always unmake it. A picker must never be able
   * to remove itself.
   */
  const hasSelection = (getCampus() ?? ALL_CAMPUSES) !== ALL_CAMPUSES;
  if (list.length === 1 && !hasSelection) {
    return (
      <span className="campus-picker campus-picker--fixed" title="The campus you work at">
        {list[0].name}
      </span>
    );
  }

  /**
   * ⚠ "ALL CAMPUSES" MEANS ALL OF *THEIRS*, AND THE LABEL HAS TO SAY SO OR IT LIES. For a trustee
   * that is every campus in the trust. For the headmaster above it is his two schools — and if this
   * read "All campuses" he would take a total that excludes the PU college as though it included
   * it, and go looking for children who were never in his figure.
   *
   * The server enforces the same reading: an "all" selection resolves to the caller's own permitted
   * set, never to an unfiltered query. See CampusScope.Filter.
   */
  const trustWide = role === 'TrustAdmin' || role === 'Owner';
  const allLabel = trustWide ? 'All campuses' : `All ${list.length} of mine`;

  const change = (value: string) => {
    setChosen(value);
    setCampus(value === ALL_CAMPUSES ? null : value);
    /**
     * ⚠ A FULL RELOAD, ON PURPOSE, AND IT IS THE HONEST CHOICE TODAY.
     *
     * Changing campus invalidates every list, count and total on screen. Re-fetching "the pages
     * that care" would mean knowing which ones do — and right now most screens do not scope at all,
     * so a soft refresh would leave some panels showing the old campus and some the new, with
     * nothing saying which is which. A reload is slower and cannot be half-right.
     *
     * When the queries are scoped and the data layer can invalidate properly, this becomes a state
     * change. Not before.
     */
    window.location.reload();
  };

  /**
   * ═══ HOW A CAMPUS IS NAMED IN THE LIST ═══
   *
   * ⚠ THE OPTGROUP HEADING IS INVISIBLE ONCE THE DROPDOWN IS CLOSED, and that is not a detail — it
   * is the control failing at its one job. A closed <select> shows the OPTION text and nothing else.
   * A trust running "Vidyanidhi English Medium School" in four towns therefore rendered four
   * indistinguishable rows open, and a top bar that could not say which of the four you were in
   * closed — while every number on every screen was about that campus and nothing else said so.
   *
   * ⚠ SO THE TOWN IS PART OF THE OPTION TEXT, not a heading above it, and there is ONE rule rather
   * than two: more than one town → every option reads "Town · Name", sorted, no optgroups. One town
   * → plain names, because a town repeated on every line tells the reader nothing.
   *
   * ⚠ AND THE TOWN GOES IN FRONT. `Name · Harihara` looks right in source and is useless on screen:
   * the control is 264px, the names are long, and the town — the ONLY part that differs between the
   * four rows — is the first thing an ellipsis takes. A disambiguator has to sit where truncation
   * cannot reach it.
   *
   * ⚠ CITY PREFERRED OVER CAMPUS NAME. Campuses are routinely named after their town, so using the
   * campus name would produce "Sirigere Campus · Vidyanidhi…" where "Sirigere · Vidyanidhi…" says
   * the same thing in eight fewer characters of a control that is already short. The campus name is
   * the fallback for a campus with no city recorded.
   */
  const placeOf = (i: Institution) => (i.campusCity || i.campusName || '').trim();
  const towns = [...new Set(list.map(placeOf))].filter(Boolean);
  const showPlace = towns.length > 1;

  const optionLabel = (i: Institution) => {
    const place = placeOf(i);
    return showPlace && place ? `${place} · ${i.name}` : i.name;
  };

  /**
   * ⚠ SORTED BY THE LABEL THE READER ACTUALLY SEES, which is what makes the towns read together
   * without an optgroup. Sorting by a hidden field would produce an order nobody on screen can
   * account for. `en-IN` because these are Indian place names and the default collation is not.
   */
  const ordered = showPlace
    ? [...list].sort((a, b) => optionLabel(a).localeCompare(optionLabel(b), 'en-IN'))
    : list;

  /**
   * ⚠ A NATIVE SELECT CLIPS ITS CLOSED TEXT WITH NO ELLIPSIS ON MOST BROWSERS — it just stops,
   * mid-word, giving no sign there was more. The title makes the full answer recoverable on hover
   * rather than leaving "Vidyanidhi English Med" as the last word on which campus this is.
   */
  const current = list.find((i) => i.id === chosen);
  const hoverTitle = current ? optionLabel(current) : `${allLabel} — which campus you are looking at`;

  return (
    <label className="campus-picker" title={hoverTitle}>
      <span className="sr-only">Campus</span>
      <select value={chosen} onChange={(e) => change(e.target.value)} aria-label="Campus" title={hoverTitle}>
        <option value={ALL_CAMPUSES}>{allLabel}</option>
        {ordered.map((i) => <option key={i.id} value={i.id}>{optionLabel(i)}</option>)}
      </select>
    </label>
  );
}
